/**
 * Performance Table Component for Supplier Performance
 *
 * The card, pagers and row loop. The two-level header lives in
 * PerformanceTableHeader, a supplier row in PerformanceTableRow, a supplier's
 * per-customer-site rows in SiteBreakdownRows and the CR-7264 group band in
 * GroupTotalRow.
 *
 * Customer review 11 Sep (item 3/4): the rows follow the old portal's
 * model — group total (when filtered to a supplier group) → the supplier as a
 * whole → the supplier at each customer site, opened from the row's chevron.
 */

import { Fragment, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Pagination } from '../../components/ui/pagination';
import { Loader2 } from 'lucide-react';
import { SupplierProfile, SupplierRatingThreshold, OTIFGradeThreshold } from '../../lib/api';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import { selfRowOf, selfRowKey } from './selfPerformance';
import { withGroupTotals } from './groupAggregate';
import { siteSplitsByCode } from './utils';
import { GroupTotalRow } from './GroupTotalRow';
import { PerformanceTableHeader, frozenColumns } from './PerformanceTableHeader';
import { PerformanceTableRow, RowCallbacks } from './PerformanceTableRow';
import { SiteBreakdownRows } from './SiteBreakdownRows';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';
import { PerformanceSort } from './performanceSort';

interface PerformanceTableProps extends RowCallbacks {
  suppliers: SupplierProfile[];
  ratingThresholds: SupplierRatingThreshold[];
  /** CR-6269: the viewer's OTIF Grade Thresholds, colouring the OTIF % column. */
  otifBands?: OTIFGradeThreshold[];
  currency: string;
  loading?: boolean;
  /**
   * SI-16: supplier-side page — rows are the caller's own company once per
   * customer. Hides the Supplier column (it would repeat the same name), keys
   * rows and drill-downs by customer, and grades each row with that
   * customer's bands.
   */
  viewerIsSupplier?: boolean;
  /** Q8: the loaded rows span more than one supplier site (GROUP view) — show the Supplier Site column even when a picker narrows the visible rows to one. */
  showSupplierSiteColumn?: boolean;
  /**
   * CR-7264: the Supplier Group filter is set to a specific group, so the
   * table is already narrowed to that group and its total is what the user
   * asked to see. Left off in the unfiltered list, where totals for every
   * group at once would break up the ranking the page exists to show.
   */
  groupFilterActive?: boolean;
  /** Customer review 11 Sep (msg 3/4): the page's params for one site × one supplier. */
  buildSiteRowParams?: (siteId: number, supplierId: number) => Record<string, string | number | boolean>;
  /** CR-7288: params for one code under one site — the third tier. */
  buildCodeRowParams?: (siteId: number, supplierId: number, code: string | null) => Record<string, string | number | boolean>;
  /** CR-8667: the column sort in effect (null = default order) and the header click. */
  sort?: PerformanceSort | null;
  onSort?: (field: string) => void;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    hasMultiplePages: boolean;
    goToPage: (page: number) => void;
  };
}

export function PerformanceTable({
  suppliers,
  ratingThresholds,
  otifBands = [],
  currency,
  loading,
  viewerIsSupplier = false,
  showSupplierSiteColumn = false,
  groupFilterActive = false,
  buildSiteRowParams,
  buildCodeRowParams,
  sort = null,
  onSort,
  pagination,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: PerformanceTableProps) {
  const qualityLabel = useQualityMeasureLabel();
  // Per-row grade bands: the page-level thresholds on the customer page, the
  // row's own customer's thresholds on the supplier page (SI-16).
  const thresholdsFor = (s: SupplierProfile) => selfRowOf(s)?.rating_thresholds ?? ratingThresholds;
  // Q8: at GROUP view a supplier's rows span sibling sites — show which.
  const showSupplierSite = viewerIsSupplier && showSupplierSiteColumn;
  const itemLabel = viewerIsSupplier ? 'customers' : 'suppliers';
  // CR-4547: colSpans count the Warehouse column only when the tenant shows it.
  const { warehouse: showWarehouse } = useFilterVisibility();
  const { columnCount } = frozenColumns(viewerIsSupplier, showSupplierSite, showWarehouse);

  // Which suppliers have their per-site breakdown open. A new result set
  // (re-filter, another page, a view switch) starts collapsed.
  // Customer review 11 Sep (stream I): keyed on WHICH suppliers are listed,
  // not on the array's identity — the page derives `suppliers` with a fresh
  // .filter() on every render, so opening a drill-down from a site row (page
  // state) re-ran this and collapsed the breakdown behind the modal.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const resultKey = suppliers.map((s) => s.company).join(',');
  useEffect(() => {
    setExpanded(new Set());
  }, [resultKey]);
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // CR-7264: filtering to a supplier group puts that group's total above its
  // member rows — and only then. In the unfiltered list this page is a ranking
  // of suppliers, and a total banded in above every group would interrupt it.
  // Not on the supplier-side page either: there the rows are the viewer's own
  // company once per customer, so a "supplier group" total is meaningless.
  const showGroupTotals = !viewerIsSupplier && groupFilterActive;
  const rows = showGroupTotals
    ? withGroupTotals(suppliers)
    : suppliers.map((supplier) => ({ kind: 'supplier' as const, supplier }));

  // CR-8667: a refresh (a sort, a page, a filter) keeps the rows on screen,
  // dimmed, until the new ones land — the header, its sort arrow and the
  // scroll position stay put. The spinner replaced the whole table on every
  // click, so the table vanished and rebuilt itself each time. It is now
  // shown only when there is nothing on screen yet.
  const refreshing = !!loading && suppliers.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {viewerIsSupplier ? 'Your Performance by Customer' : 'Supplier Performance Overview'} ({pagination.totalCount})
          {refreshing && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Updating" />}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading && !refreshing ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : suppliers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">{viewerIsSupplier ? 'No customers found' : 'No suppliers found'}</p>
        ) : (
          <>
            {/* Top Pagination */}
            {pagination.hasMultiplePages && (
              <div className="px-6 pt-4">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={pagination.goToPage}
                  itemLabel={itemLabel}
                />
              </div>
            )}

            <div
              className={`overflow-x-auto overflow-y-auto max-h-[70vh] transition-opacity ${refreshing ? 'opacity-60' : ''}`}
              aria-busy={refreshing}
            >
              <table className="w-full min-w-[2680px]">
                <PerformanceTableHeader
                  viewerIsSupplier={viewerIsSupplier}
                  showSupplierSite={showSupplierSite}
                  qualityLabel={qualityLabel}
                  sort={sort}
                  onSort={onSort}
                />
                <tbody>
                  {rows.map((entry) => {
                    if (entry.kind === 'group') {
                      return (
                        <GroupTotalRow
                          key={`group-${entry.group.groupId}`}
                          group={entry.group}
                          colSpan={columnCount}
                          qualityLabel={qualityLabel}
                          currency={currency}
                        />
                      );
                    }
                    const supplier = entry.supplier;
                    const sites = supplier.customer_sites ?? [];
                    // CR-7288 round 3 (blind stage 2): a supplier with ONE site
                    // whose site splits by code was unreachable — the site tier
                    // never opened, so the code tier beneath it could not mount,
                    // while the EXPORT wrote those code rows anyway. File and
                    // page disagreed about rows that exist. Not live on this
                    // tenant (every multi-code relationship belongs to a supplier
                    // with 3-7 sites), which is why no shot could catch it.
                    const expandable = !viewerIsSupplier && !!buildSiteRowParams
                      && (sites.length > 1 || (sites.length === 1 && siteSplitsByCode(sites[0])));
                    const isOpen = expandable && expanded.has(supplier.company);
                    return (
                      <Fragment key={selfRowKey(supplier)}>
                        <PerformanceTableRow
                          supplier={supplier}
                          thresholds={thresholdsFor(supplier)}
                          otifBands={otifBands}
                          currency={currency}
                          viewerIsSupplier={viewerIsSupplier}
                          showSupplierSite={showSupplierSite}
                          expandable={expandable}
                          expanded={isOpen}
                          onToggleExpand={() => toggle(supplier.company)}
                          onOpenGRNModal={onOpenGRNModal}
                          onOpenNCRModal={onOpenNCRModal}
                          onOpenPOLineModal={onOpenPOLineModal}
                        />
                        {isOpen && buildSiteRowParams && buildCodeRowParams && (
                          <SiteBreakdownRows
                            supplier={supplier}
                            thresholds={thresholdsFor(supplier)}
                            otifBands={otifBands}
                            currency={currency}
                            columnCount={columnCount}
                            buildSiteRowParams={buildSiteRowParams}
                          buildCodeRowParams={buildCodeRowParams}
                            onOpenGRNModal={onOpenGRNModal}
                            onOpenNCRModal={onOpenNCRModal}
                            onOpenPOLineModal={onOpenPOLineModal}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Bottom Pagination */}
            {pagination.hasMultiplePages && (
              <div className="px-6 pb-4">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={pagination.goToPage}
                  itemLabel={itemLabel}
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
